import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PainPointSequence from './PainPointSequence';
import SkipEyeLogoTargeted from './SkipEyeLogoTargeted';
import SkipEyeLogoAnimated from './SkipEyeLogoAnimated';
import { useResponsive } from '../hooks/useResponsive';

const NewCustomer = () => {
  const navigate = useNavigate();
  const viewport = useResponsive();
  const [showPainPoints, setShowPainPoints] = useState(true);
  const [showSolution, setShowSolution] = useState(false);
  const [targetPosition, setTargetPosition] = useState(null);

  // Calculate logo scale based on device type (same as LandingPage)
  const getLogoScale = () => {
    if (viewport.width < 480) return 0.4;
    if (viewport.width < 768) return 0.5;
    if (viewport.width < 1024) return 0.65;
    return 0.7;
  };

  const logoScale = getLogoScale();

  const handleBackClick = () => {
    navigate('/');
  };

  const handlePainPointsComplete = () => {
    setShowPainPoints(false);
    // Short delay before showing solution
    setTimeout(() => {
      setShowSolution(true);
    }, 500);
  };

  const handlePositionChange = (position) => {
    setTargetPosition(position);
  };

  const styles = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      minHeight: '100vh',
      backgroundColor: '#f0f0f0',
      fontFamily: 'Arial, sans-serif',
      padding: viewport.isMobile ? 'var(--space-md) var(--space-sm)' : '40px 20px',
      paddingTop: viewport.isMobile ? 'var(--space-xl)' : '120px'
    },
    header: {
      display: 'flex',
      flexDirection: viewport.isMobile ? 'column' : 'row',
      alignItems: viewport.isMobile ? 'flex-start' : 'center',
      marginBottom: viewport.isMobile ? 'var(--space-md)' : '40px',
      gap: viewport.isMobile ? 'var(--space-sm)' : '20px',
      position: 'relative'
    },
    backButton: {
      padding: viewport.isMobile ? '10px 20px' : '12px 24px',
      fontSize: 'var(--text-base)',
      fontWeight: '500',
      backgroundColor: '#7dd321',
      color: 'white',
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      minHeight: '44px',
      position: viewport.isMobile ? 'absolute' : 'relative',
      top: viewport.isMobile ? 'var(--safe-top)' : 'auto',
      left: viewport.isMobile ? 'var(--safe-left)' : 'auto'
    },
    backButtonHover: {
      backgroundColor: '#5cb41b',
      transform: 'translateY(-1px)',
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
    },
    title: {
      fontSize: viewport.isMobile ? 'var(--text-xl)' : 'var(--text-2xl)',
      fontWeight: 'bold',
      color: '#5cb41b',
      margin: 0,
      marginTop: viewport.isMobile ? '60px' : 0
    },
    content: {
      maxWidth: viewport.isMobile ? '100%' : '800px',
      width: viewport.isMobile ? '100%' : 'auto',
      margin: '0 auto',
      backgroundColor: 'white',
      padding: viewport.isMobile ? 'var(--space-lg)' : 'clamp(24px, 5vw, 60px)',
      borderRadius: viewport.isMobile ? '8px' : '12px',
      boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
      textAlign: 'center'
    },
    subtitle: {
      fontSize: viewport.isMobile ? 'var(--text-lg)' : 'var(--text-xl)',
      color: '#666',
      marginBottom: viewport.isMobile ? 'var(--space-md)' : '30px'
    },
    placeholder: {
      fontSize: viewport.isMobile ? 'var(--text-base)' : 'var(--text-lg)',
      color: '#888',
      lineHeight: viewport.isMobile ? '1.7' : '1.6',
      marginBottom: viewport.isMobile ? 'var(--space-lg)' : '40px'
    },
    contactInfo: {
      backgroundColor: '#f8f9fa',
      padding: viewport.isMobile ? 'var(--space-md)' : '30px',
      borderRadius: '8px',
      marginTop: viewport.isMobile ? 'var(--space-lg)' : '40px'
    },
    contactTitle: {
      fontSize: viewport.isMobile ? 'var(--text-lg)' : 'var(--text-xl)',
      fontWeight: '600',
      color: '#5cb41b',
      marginBottom: '15px'
    },
    contactText: {
      fontSize: viewport.isMobile ? 'var(--text-sm)' : 'var(--text-base)',
      color: '#666',
      marginBottom: '10px'
    },
    contactLink: {
      color: '#7dd321',
      textDecoration: 'none',
      fontWeight: '500',
      minHeight: '44px',
      display: 'inline-block',
      lineHeight: '44px'
    },
    animationRow: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: '50px',
      width: '100%',
      minHeight: '80px'
    }
  };

  return (
    <>
      {/* Pain Points Sequence with Mouse-Tracking Logo */}
      {showPainPoints && (
        <>
          {/* Background with centered logo */}
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: '#f0f0f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100
          }}>
            <div style={{
              transform: `scale(${logoScale})`,
              transformOrigin: 'center center'
            }}>
              <SkipEyeLogoTargeted size="large" targetPosition={targetPosition} />
            </div>
          </div>

          {/* Pain points overlay */}
          <PainPointSequence
            onComplete={handlePainPointsComplete}
            onPositionChange={handlePositionChange}
          />
        </>
      )}

      {/* Main Content - Shows after pain points */}
      {showSolution && (
        <div style={styles.container}>
          <div style={styles.header}>
            <button
              style={styles.backButton}
              onClick={handleBackClick}
              onMouseEnter={(e) => {
                Object.assign(e.target.style, styles.backButtonHover);
              }}
              onMouseLeave={(e) => {
                Object.assign(e.target.style, styles.backButton);
              }}
            >
              ← Back
            </button>
            <h1 style={styles.title}>SkipEye</h1>
          </div>

          <div style={{
            ...styles.content,
            opacity: showSolution ? 1 : 0,
            transform: showSolution ? 'scale(1)' : 'scale(0.95)',
            transition: 'all 0.6s ease-in-out'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              marginBottom: '40px'
            }}>
              <SkipEyeLogoAnimated size="large" />
            </div>

            <h2 style={styles.subtitle}>The Solution</h2>

            <p style={{
              fontSize: '24px',
              fontWeight: '500',
              color: '#333',
              marginBottom: '30px',
              lineHeight: '1.6'
            }}>
              A simple yet powerful tool to view your skips from the sky
            </p>

            <p style={styles.placeholder}>
              SkipEye provides real-time visibility of all your skip locations with aerial imagery,
              helping you stay on top of every job without the guesswork.
            </p>

            <div style={styles.contactInfo}>
              <h3 style={styles.contactTitle}>Get Started Today</h3>
              <p style={styles.contactText}>
                Ready to take control? Contact our team to discuss your requirements and get started with a 2 week free trial.
              </p>
              <p style={styles.contactText}>
                Email: <a href="mailto:info@skipeye.com?subject=New Customer Enquiry" style={styles.contactLink}>info@skipeye.com</a>
              </p>
              <p style={styles.contactText}>
                Subject: New Customer Enquiry
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default NewCustomer;